import type { SessionInferenceMode } from "@/components/graphql/schema.graphql";
import { getIsEditImageTargetFileValid } from "@/components/InferenceFormComposer/FieldDefs/fieldDefs/SourceImages";
import {
  ReferenceImageType,
  ReferenceMeshType,
  ReferenceVideoType,
} from "@/components/ui-v2/References/types";
import { isImageWithinInputDimension } from "@/hooks/fileImageActions/utils";

export type RefinementValidatorInput =
  | ReferenceImageType
  | ReferenceVideoType
  | ReferenceMeshType;

const isReferenceImageType = (
  ref: RefinementValidatorInput,
): ref is ReferenceImageType => "width" in ref && "height" in ref;

const isReferenceVideoType = (
  ref: RefinementValidatorInput,
): ref is ReferenceVideoType =>
  "width" in ref && "height" in ref && !("transform" in ref);

const isReferenceMeshType = (
  ref: RefinementValidatorInput,
): ref is ReferenceMeshType => !("width" in ref);

// Video refinement dimension limits (previously in deleted form constants)
const VIDEO_MAX_WIDTH = 3840;
const VIDEO_MAX_HEIGHT = 2160;
const VIDEO_MIN_UPSCALE_FACTOR = 1;
const VIDEO_MAX_REMOVE_BG_DIMENSION = 4096;

// TODO refactor once the forms are migrated to the new composer,
// lots of duplication here.
export const REFINEMENT_VALIDATORS: Partial<
  Record<SessionInferenceMode, (ref: RefinementValidatorInput) => boolean>
> = {
  UPSCALE: (ref) =>
    isReferenceImageType(ref) && !!isImageWithinInputDimension(ref),
  REMOVE_BACKGROUND: (ref) =>
    isReferenceImageType(ref) &&
    getIsEditImageTargetFileValid(ref.width, ref.height),
  VECTORIZE_IMAGE: (ref) =>
    isReferenceImageType(ref) &&
    getIsEditImageTargetFileValid(ref.width, ref.height),
  ADD_SFX_VIDEO: (ref) => isReferenceVideoType(ref),
  REFRAME_VIDEO: (ref) =>
    isReferenceVideoType(ref) &&
    ref.width <= VIDEO_MAX_WIDTH &&
    ref.height <= VIDEO_MAX_HEIGHT,
  REMOVE_BACKGROUND_VIDEO: (ref) =>
    isReferenceVideoType(ref) &&
    ref.width <= VIDEO_MAX_REMOVE_BG_DIMENSION &&
    ref.height <= VIDEO_MAX_REMOVE_BG_DIMENSION,
  UPSCALE_VIDEO: (ref) =>
    isReferenceVideoType(ref) &&
    ref.width * VIDEO_MIN_UPSCALE_FACTOR <= VIDEO_MAX_WIDTH &&
    ref.height * VIDEO_MIN_UPSCALE_FACTOR <= VIDEO_MAX_HEIGHT,
  RETEXTURE_MESH: (ref) => isReferenceMeshType(ref) && !!ref.src,
  RIG_MESH: (ref) => isReferenceMeshType(ref) && !!ref.src,
  REMESH: (ref) => isReferenceMeshType(ref) && !!ref.src,
};

export const GENERIC_ERROR = "There was an error. Please try again.";
